/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {HashLocationStrategy, LocationStrategy} from '@angular/common';
import {BrowserModule} from '@angular/platform-browser';
import {NgModule, ErrorHandler, CUSTOM_ELEMENTS_SCHEMA, APP_INITIALIZER, Injectable} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {NgxSpinnerModule} from 'ngx-spinner';
import {DataTableModule} from "../assets/design-library/data-table";
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {AuthGuard} from './core/auth/auth-guard';
import {AuthInterceptor} from './core/auth/auth.interceptor';
import {AuthenticationModule} from './core/auth/authentication.module';
import {AuthenticationService} from './core/auth/authentication.service';
import {CoreModule} from './core/core.module';
import {DesignLibraryModule} from '../assets/design-library/design-library.module';
import {AccidentListComponent} from './accidentList/accidentList.component';
import {HomeComponent} from './home/home.component';
import {DriverDataTable} from "./home/driver-data-table/driver-data-table";
import {HttpClientModule, HttpClient, HTTP_INTERCEPTORS} from "@angular/common/http";
import {MessagesComponent} from "./messages/messages.component";
import {DriverDetailComponent} from "./driver-detail/driver-detail.component";
import {MessageService} from "./services/message.service";
import {GmPersonService} from "./services/gm-person.service";
import {DriverService} from "./services/driver.service";
import {DriverSearchComponent} from "./home/driverSearch.component";
import {DynamicComponentContainerDirective} from "./home/dynamic-component-container.directive";
import {DriverSearchAddComponent} from './admin/driver-search-add/driver-search-add.component';
import {TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import {EmployeeTypeService} from "./services/employee-type.service";
import {DriverComponentContainerDirective} from "./admin/driver-search-add/driver-component-container.directive";
import {GMINSearchComponent} from "./admin/driver-search-add/GMINSearch.component";
import {DriverEditableTable} from "./admin/driver-search-add/driver-editable-table/driver-editable-table";
import {SitesComponent} from './admin/sites/sites.component';
import {SitesService} from "./services/sites.service";
import {TrainingComponent} from './admin/training/training.component';
import {TrainingService} from "./services/training.service";
import {UserAuthService} from "./services/user-auth.service";
import {AuthResolver} from "./services/auth.resolver";
import {AppLoadModule} from "./app-load/app-load.module";
import {EndorsementService} from "./services/endorsement.service";
import {EndorsementsComponent} from './admin/endorsements/endorsements.component';
import {DrivingLevelComponent} from './admin/driving-level/driving-level.component';
import {DrivingLevelsService} from "./services/driving-levels.service";
import {SiteDetailComponent} from './admin/site-detail/site-detail.component';
import {ViewReportComponent} from './reports/view-report/view-report.component';
import {ViolationDefinitionComponent} from './admin/violation-definition/violation-definition.component';
import {ViolationDefinitionService} from "./services/violation-definition.service";
import {LocationsComponent} from './admin/locations/locations.component';
import {LocationsService} from "./services/locations.service";
import {LocationTypesService} from "./services/location-types.service";
import {GovermentEndorsementsService} from "./services/goverment-endorsements.service";
import {RegionService} from "./services/region.service";
import {AccidentDetailComponent} from "./accident-detail/accident-detail.component";
import {AccidentsService} from "./services/accidents.service";
import {AccidentClassificationService} from "./services/accident-classification.service";
import {AccidentCausationService} from "./services/accident-causation.service";
import {InjuryClassificationService} from "./services/injury-classification.service";
import {CrashObjectDefinitionService} from "./services/crash-object-definition.service";
import {VehicleDirectionDefinitionService} from "./services/vehicle-direction-definition.service";
import {AccidentPersonTypeService} from "./services/accident-person-type.service";
import {PersonService} from "./services/person.service";
import {PersonDetailComponent } from './person-detail/person-detail.component';
import {EcsUploadDownloadService} from "./services/ecs-upload-download.service";
import {SiteBaseMileageService} from "./services/site-base-mileage.service";
import {LicenseTypeService} from './services/license-type.service';
import {GovernmentEndorsementsComponent} from './admin/endorsements-gov/endorsements-gov.component';
import {RegionsComponent} from './admin/regions/regions.component';
import {LicenseTypesComponent} from './admin/license-type/license-types.component';
import {LocationTypeComponent} from './admin/location-type/location-type.component';
import {PageNoFoundComponent } from './page-no-found/page-no-found.component';
import {AccidentClassificationComponent} from './admin/accident-classification/accident-classification.component';
import { AccidentCausationComponent } from './admin/accident-causation/accident-causation.component';
import { AccidentCrashObjectComponent } from './admin/accident-crash-object/accident-crash-object.component';
import { VehicleDirectionComponent } from './admin/vehicle-direction/vehicle-direction.component';
import { AccidentPersonTypeComponent } from './admin/accident-person-type/accident-person-type.component';
import { InjuryClassificationComponent } from './admin/injury-classification/injury-classification.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { GlobalErrorHandler } from './global-error-handler';

@NgModule({
  imports: [
    AppLoadModule,
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    AuthenticationModule,
    CoreModule,
    DataTableModule,
    DesignLibraryModule,
    FormsModule,
    HttpClientModule,
    ReactiveFormsModule,
    TranslateModule.forRoot(),
    NgxSpinnerModule,
    ReactiveFormsModule,
    NgbModule,
    BrowserAnimationsModule,

  ],
  declarations: [
    AppComponent,
    HomeComponent,
    AccidentListComponent,
    DriverDataTable,
    DriverEditableTable,
    MessagesComponent,
    DriverDetailComponent,
    DriverSearchComponent,
    DynamicComponentContainerDirective,
    DriverSearchAddComponent,
    GMINSearchComponent,
    DriverComponentContainerDirective,
    LicenseTypesComponent,
    RegionsComponent,
    SitesComponent,
    TrainingComponent,
    EndorsementsComponent,
    GovernmentEndorsementsComponent,
    DrivingLevelComponent,
    SiteDetailComponent,
    ViewReportComponent,
    ViolationDefinitionComponent,
    LocationsComponent,
    LocationTypeComponent,
    AccidentDetailComponent,
    PersonDetailComponent,
    PageNoFoundComponent,
    AccidentClassificationComponent,
    AccidentCausationComponent,
    AccidentCrashObjectComponent,
    VehicleDirectionComponent,
    AccidentPersonTypeComponent,
    InjuryClassificationComponent
  ],
  exports: [DriverDataTable, DriverEditableTable,TranslateModule],
  providers: [
    ...provideTranslateHttpLoader(),
    AuthGuard,
    AuthenticationService,
    GmPersonService,
    MessageService,
    DriverService,
    RegionService,
    SitesService,
    TrainingService,
    EndorsementService,
    DrivingLevelsService,
    UserAuthService,
    AuthResolver,
    ViolationDefinitionService,
    LocationsService,
    LocationTypesService,
    GovermentEndorsementsService,
    LicenseTypeService,
    AccidentsService,
    AccidentClassificationService,
    AccidentCausationService,
    InjuryClassificationService,
    CrashObjectDefinitionService,
    VehicleDirectionDefinitionService,
    AccidentPersonTypeService,
    PersonService,
    EmployeeTypeService,
    RegionService,
    EcsUploadDownloadService,
    SiteBaseMileageService,
    {provide: LocationStrategy, useClass: HashLocationStrategy},
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    }
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {
}
